import React, { useEffect, useMemo, useState } from "react";
import { Toaster } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import Header from "../../../components/Header";
import FirmFlowTable from "../../../components/mytasks/FirmFlowTable";
import Sidebar from "../../../components/Sidebar";
import { normalizeClientServiceInterval } from "../../../helpers/intervalTypesMap";
import { getEmployeeServices } from "../../../services/analytics/getEmployeeServices";
import {
    DEFAULT_GUID,
    getEmployeeServicesFiltersFromSearchParams,
} from "./employeeProductionFilters";

function EmployeeServices() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [services, setServices] = useState([]);
    const [name, setName] = useState("");
    const [totalServices, setTotalServices] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [tooltip, showTooltip] = useState(true);
    const [searchParams] = useSearchParams();
    const filters = useMemo(
        () => getEmployeeServicesFiltersFromSearchParams(searchParams),
        [searchParams]
    );

    useEffect(() => {
        const fetchServices = async () => {
            setIsLoading(true);
            setError("");
            setServices([]);
            setTotalServices(0);

            try {
                const response = await getEmployeeServices(filters);

                if (response?.success) {
                    setServices(
                        (response.services || []).map((service) => ({
                            ...service,
                            interval: normalizeClientServiceInterval(
                                service.interval,
                            ),
                        })),
                    );
                    setName(response.employeeName || "");
                    setTotalServices(response.totalServices || 0);
                } else {
                    setError(
                        response?.message || "Unable to load Employee Services.",
                    );
                }
            } catch (requestError) {
                setError(
                    requestError?.response?.data?.message ||
                        "Unable to load Employee Services.",
                );
            } finally {
                setIsLoading(false);
            }
        };
        fetchServices();
    }, [filters]);

    const columns = useMemo(
        () => [
            {
                Header: "Service",
                accessor: "service",
                Cell: (props) => (
                    <>
                        {tooltip && (
                            <ReactTooltip
                                type="light"
                                effect="solid"
                                place="top"
                                border={true}
                                borderColor={"rgb(220,220,220)"}
                                className="rounded-md bg-gray-50"
                                multiline={false}
                            />
                        )}
                        <a
                            href={`/clients/service/${props.row.original.id}/client/${props.row.original.clientId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onMouseEnter={() => showTooltip(true)}
                            onMouseLeave={() => showTooltip(false)}
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {props.value}
                        </a>
                    </>
                ),
            },
            {
                Header: "Client",
                accessor: "client",
                Cell: (props) => (
                    <>
                        {tooltip && (
                            <ReactTooltip
                                type="light"
                                effect="solid"
                                place="top"
                                border={true}
                                borderColor={"rgb(220,220,220)"}
                                className="rounded-md bg-gray-50"
                                multiline={false}
                            />
                        )}
                        <a
                            href={`/clients/service/${props.row.original.id}/client/${props.row.original.clientId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onMouseEnter={() => showTooltip(true)}
                            onMouseLeave={() => showTooltip(false)}
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {props.value}
                        </a>
                    </>
                ),
            },
            {
                Header: "Interval",
                accessor: "interval",
                Cell: (props) => props.value,
            },
        ],
        []
    );

    return (
        <div className="flex h-screen overflow-hidden">
            <Toaster />
            {/* Sidebar */}
            <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

            {/* Content area */}
            <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                {/*  Site header */}
                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <main>
                    <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                        {/* Page header */}
                        <div className="mb-8 sm:flex sm:justify-between sm:items-center">
                            {/* Left: Title */}
                            <div className="mb-4 sm:mb-0">
                                <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                    {totalServices} Services assigned to{" "}
                                    {filters.employeeId !== DEFAULT_GUID && `${name}`}{" "}
                                </h1>
                            </div>
                        </div>

                        {error && (
                            <div className="p-3 mb-6 text-sm text-red-700 border border-red-200 rounded-lg bg-red-50">
                                {error}
                            </div>
                        )}

                        {/* Table */}
                        {!isLoading && !error && (
                            <FirmFlowTable
                                data={services}
                                columns={columns}
                                resetPageSignal={filters.includeInactive}
                            />
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}

export default EmployeeServices;
